import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Trophy, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Target,
  Zap,
  Shield,
  Award
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryConfig = {
  critical_action: {
    label: 'Critical Action',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  standard_care: {
    label: 'Standard Care',
    icon: Shield,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  standard_error: {
    label: 'Standard Error',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  critical_error: {
    label: 'Critical Error',
    icon: AlertTriangle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  }
};

const tierConfig = {
  'Platinum Clinician': {
    gradient: 'from-violet-600 to-purple-700',
    bgColor: 'bg-gradient-to-r from-violet-100 to-purple-100',
    borderColor: 'border-violet-300',
    textColor: 'text-violet-800',
    icon: '🏆'
  },
  'Gold Clinician': {
    gradient: 'from-yellow-500 to-amber-600',
    bgColor: 'bg-gradient-to-r from-yellow-100 to-amber-100',
    borderColor: 'border-yellow-300',
    textColor: 'text-yellow-800',
    icon: '🥇'
  },
  'Silver Clinician': {
    gradient: 'from-slate-400 to-slate-500',
    bgColor: 'bg-gradient-to-r from-slate-100 to-slate-200',
    borderColor: 'border-slate-300',
    textColor: 'text-slate-700',
    icon: '🥈'
  },
  'Novice Tier': {
    gradient: 'from-slate-500 to-slate-600',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    textColor: 'text-slate-600',
    icon: '📚'
  }
};

// Fallback for legacy tier names
const getTierConfig = (tierName) => {
  if (tierConfig[tierName]) return tierConfig[tierName];
  // Legacy fallbacks
  if (tierName === 'Expert') return tierConfig['Platinum Clinician'];
  if (tierName === 'Competent') return tierConfig['Gold Clinician'];
  if (tierName === 'Needs Review') return tierConfig['Novice Tier'];
  return {
    gradient: 'from-slate-500 to-slate-600',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    textColor: 'text-slate-600',
    icon: '📋'
  };
};

export default function CaseStudyResults() {
  const navigate = useNavigate();
  const { caseId, attemptId } = useParams();
  const location = useLocation();
  
  const [results, setResults] = useState(location.state?.results || null);
  const [loading, setLoading] = useState(!location.state?.results);
  const [error, setError] = useState(null);
  const [expandedStages, setExpandedStages] = useState({});

  useEffect(() => {
    if (!results) {
      fetchResults();
    } else {
      const expanded = {};
      results.debrief?.forEach((_, idx) => {
        expanded[idx] = true;
      });
      setExpandedStages(expanded);
    }
  }, [results]);

  const fetchResults = async () => {
    try {
      const res = await fetch(`${API}/api/case-studies/${caseId}/results/${attemptId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch results');
      const data = await res.json();
      setResults(data);
      
      const expanded = {};
      data.debrief?.forEach((_, idx) => {
        expanded[idx] = true;
      });
      setExpandedStages(expanded);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleStage = (idx) => {
    setExpandedStages(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading results...</div>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="bg-white border-slate-200 max-w-md">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Error Loading Results</h3>
            <p className="text-slate-500 mb-4">{error || 'Results not found'}</p>
            <Button onClick={() => navigate('/case-studies')} variant="outline">
              Back to Case Studies
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentTierConfig = getTierConfig(results.performance_tier);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/case-studies')}
            className="text-slate-600 hover:text-slate-900"
            data-testid="back-to-cases"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Case Studies
          </Button>
        </div>
      </div>

      {/* Case Title */}
      <div className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-3">
            <Trophy className="w-6 h-6 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="case-title">
            {results.case_title}
          </h1>
          <p className="text-slate-500 mt-1">Case Completed!</p>
        </div>
      </div>

      {/* PROMINENT Performance Tier Section */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className={`${currentTierConfig.bgColor} ${currentTierConfig.borderColor} border-2 shadow-lg mb-8`}>
          <CardContent className="py-8">
            <div className="text-center">
              <div className="text-5xl mb-4">{currentTierConfig.icon}</div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Award className={`w-8 h-8 ${currentTierConfig.textColor}`} />
                <h2 className={`text-3xl font-bold ${currentTierConfig.textColor}`} data-testid="performance-tier">
                  {results.performance_tier}
                </h2>
              </div>
              <p className="text-lg text-slate-600 max-w-md mx-auto">
                {results.tier_description}
              </p>
            </div>
            
            {/* Score Summary */}
            <div className="grid grid-cols-3 gap-6 mt-8 pt-6 border-t border-slate-200/50">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-800">{results.total_score}</div>
                <div className="text-sm text-slate-500">Points Earned</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold ${currentTierConfig.textColor}`}>{results.percent_score}%</div>
                <div className="text-sm text-slate-500">Score</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-800">{results.max_possible_score}</div>
                <div className="text-sm text-slate-500">Max Points</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinical Debrief */}
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-slate-800">
          <Target className="w-5 h-5 text-purple-600" />
          Clinical Debrief
        </h2>

        <div className="space-y-4">
          {results.debrief?.map((item, idx) => (
            <Collapsible
              key={idx}
              open={expandedStages[idx]}
              onOpenChange={() => toggleStage(idx)}
            >
              <Card className="bg-white border-slate-200 overflow-hidden">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                          Stage {item.stage_number}
                        </Badge>
                        <CardTitle className="text-slate-900 text-left">
                          {item.stage_title}
                        </CardTitle>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className={`font-bold ${item.question_score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {item.question_score >= 0 ? '+' : ''}{item.question_score}
                          </span>
                          <span className="text-slate-400"> / {item.question_max}</span>
                        </div>
                        {expandedStages[idx] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <CardContent className="border-t border-slate-100 pt-4">
                    {/* Vignette */}
                    <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="text-xs text-slate-500 uppercase mb-2">Clinical Scenario</div>
                      <p className="text-slate-700">{item.vignette}</p>
                    </div>

                    {/* Question */}
                    <div className="mb-4">
                      <div className="text-xs text-slate-500 uppercase mb-2">Question</div>
                      <p className="text-slate-900 font-medium">{item.question_prompt}</p>
                    </div>

                    {/* All Options with Results */}
                    <div className="space-y-3">
                      {item.all_options?.map((option) => {
                        const config = categoryConfig[option.category] || categoryConfig.standard_care;
                        const Icon = config.icon;
                        const isPositive = option.point_value > 0;
                        const isNegative = option.point_value < 0;
                        
                        return (
                          <div
                            key={option.option_id}
                            className={`
                              p-4 rounded-lg border
                              ${option.was_selected
                                ? isPositive
                                  ? 'bg-green-50 border-green-300'
                                  : isNegative
                                    ? 'bg-red-50 border-red-300'
                                    : 'bg-slate-50 border-slate-300'
                                : 'bg-white border-slate-200'
                              }
                            `}
                            data-testid={`debrief-option-${option.option_id}`}
                          >
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex items-start gap-3">
                                {option.was_selected ? (
                                  isPositive ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                  ) : isNegative ? (
                                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <CheckCircle2 className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                                  )
                                ) : (
                                  <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex-shrink-0 mt-0.5" />
                                )}
                                <span className={option.was_selected ? 'text-slate-900 font-medium' : 'text-slate-600'}>
                                  {option.text}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Badge className={`${config.bgColor} ${config.color} ${config.borderColor}`}>
                                  <Icon className="w-3 h-3 mr-1" />
                                  {config.label}
                                </Badge>
                                <span className={`font-bold ${
                                  option.point_value > 0 ? 'text-green-600' : 
                                  option.point_value < 0 ? 'text-red-600' : 'text-slate-500'
                                }`}>
                                  {option.point_value > 0 ? '+' : ''}{option.point_value}
                                </span>
                              </div>
                            </div>
                            
                            {/* Rationale */}
                            <div className="ml-8 mt-2 text-sm text-slate-600 italic">
                              {option.rationale}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button
            variant="outline"
            onClick={() => navigate('/case-studies')}
            className="border-slate-300 text-slate-700 hover:bg-slate-100"
            data-testid="browse-more-btn"
          >
            Browse More Cases
          </Button>
          <Button
            onClick={() => navigate(`/case-studies/${caseId}`)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="retry-case-btn"
          >
            Try This Case Again
          </Button>
        </div>
      </div>
    </div>
  );
}
